import React, { useState } from 'react';

const Documentation = () => {
  const [activeTab, setActiveTab] = useState('scoring');

  return (
    <div className="max-w-6xl mx-auto bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white">
        <h1 className="text-3xl font-bold mb-2">📚 Help & Documentation</h1>
        <p className="text-blue-100">Complete guide to using the EHS Risk-Based Staffing Modeler Pro</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="flex space-x-2 px-8">
          <button
            onClick={() => setActiveTab('scoring')}
            className={`px-6 py-4 font-semibold transition-colors ${
              activeTab === 'scoring'
                ? 'border-b-4 border-blue-600 text-blue-600 bg-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📊 Activity Scoring Guide
          </button>
          <button
            onClick={() => setActiveTab('workflow')}
            className={`px-6 py-4 font-semibold transition-colors ${
              activeTab === 'workflow'
                ? 'border-b-4 border-blue-600 text-blue-600 bg-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🔄 Workflow Guide
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`px-6 py-4 font-semibold transition-colors ${
              activeTab === 'reports'
                ? 'border-b-4 border-blue-600 text-blue-600 bg-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📄 Reports & Export
          </button>
          <button
            onClick={() => setActiveTab('faq')}
            className={`px-6 py-4 font-semibold transition-colors ${
              activeTab === 'faq'
                ? 'border-b-4 border-blue-600 text-blue-600 bg-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            ❓ FAQ
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-8">
        {/* Activity Scoring Guide Tab */}
        {activeTab === 'scoring' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Activity Scoring Guide</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Each EHS activity is scored across three dimensions to calculate the actual workload required. 
                These scores combine to determine the weighted hours, which directly impact your FTE requirements.
              </p>
            </div>

            {/* Complexity Section */}
            <div className="border-l-4 border-blue-500 pl-6">
              <h3 className="text-xl font-bold text-blue-900 mb-3">Complexity % (0-100)</h3>
              <p className="text-gray-700 mb-4">
                <strong>Measures:</strong> Technical difficulty, regulatory requirements, and expertise needed
              </p>
              
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-900">Low Complexity (0-25%)</h4>
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">BASIC</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">Simple, routine tasks with clear procedures</p>
                  <p className="text-xs text-gray-600"><strong>Examples:</strong> Fire extinguisher inspections, basic safety training, PPE inventory, routine safety meetings</p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-900">Medium Complexity (26-60%)</h4>
                    <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold">MODERATE</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">Moderate expertise, specialized training needed</p>
                  <p className="text-xs text-gray-600"><strong>Examples:</strong> Hazard communication, industrial hygiene sampling, permit compliance, OSHA recordkeeping</p>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-900">High Complexity (61-100%)</h4>
                    <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">ADVANCED</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">Highly technical, advanced certifications required</p>
                  <p className="text-xs text-gray-600"><strong>Examples:</strong> Process Safety Management, radiation safety, BSL-3 labs, RCRA hazardous waste programs</p>
                </div>
              </div>
            </div>

            {/* Risk Section */}
            <div className="border-l-4 border-orange-500 pl-6">
              <h3 className="text-xl font-bold text-orange-900 mb-3">Risk % (0-100)</h3>
              <p className="text-gray-700 mb-4">
                <strong>Measures:</strong> Potential severity of consequences from non-compliance or incidents
              </p>
              
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-900">Low Risk (0-25%)</h4>
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">MINIMAL</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">Minor injuries only, minimal operational impact</p>
                  <p className="text-xs text-gray-600"><strong>Examples:</strong> Office ergonomics, housekeeping, wellness programs, parking lot safety</p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-900">Medium Risk (26-60%)</h4>
                    <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold">MODERATE</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">Recordable injuries possible, regulatory violations likely</p>
                  <p className="text-xs text-gray-600"><strong>Examples:</strong> Forklift safety, machine guarding, confined space entry, lockout/tagout (LOTO)</p>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-900">High Risk (61-100%)</h4>
                    <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">CRITICAL</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">Fatality potential, catastrophic incidents possible</p>
                  <p className="text-xs text-gray-600"><strong>Examples:</strong> PSM/RMP programs, high-voltage electrical, excavation, infectious disease control (Ebola, COVID-19)</p>
                </div>
              </div>
            </div>

            {/* Environment Section */}
            <div className="border-l-4 border-green-500 pl-6">
              <h3 className="text-xl font-bold text-green-900 mb-3">Environment % (0-100)</h3>
              <p className="text-gray-700 mb-4">
                <strong>Measures:</strong> Environmental compliance burden and ecological impact potential
              </p>
              
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-900">Low Environment (0-25%)</h4>
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">MINIMAL</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">Little/no environmental requirements</p>
                  <p className="text-xs text-gray-600"><strong>Examples:</strong> Safety training, ergonomics, workplace violence prevention, hearing conservation</p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-900">Medium Environment (26-60%)</h4>
                    <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold">MODERATE</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">Some permits/reporting, regulated waste streams</p>
                  <p className="text-xs text-gray-600"><strong>Examples:</strong> Storm water management, universal waste, small quantity generator, SPCC plans</p>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-gray-900">High Environment (61-100%)</h4>
                    <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">SIGNIFICANT</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">Multiple complex permits, major release potential</p>
                  <p className="text-xs text-gray-600"><strong>Examples:</strong> Title V air permits, RCRA large quantity generators, contaminated site remediation</p>
                </div>
              </div>
            </div>

            {/* Formula Explanation */}
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6">
              <h3 className="text-xl font-bold text-blue-900 mb-3">📐 How Scores Calculate FTE Requirements</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-gray-700 mb-2">The three percentages are <strong>additive</strong>, meaning they combine to increase base hours:</p>
                  <div className="bg-white p-4 rounded border border-blue-200 font-mono text-sm">
                    Weighted Hours = Base Hours × (1 + Complexity% + Risk% + Environment%)
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded border border-blue-200">
                  <p className="font-semibold text-gray-900 mb-2">Example:</p>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• Activity: Hazardous Waste Management</li>
                    <li>• Base Hours: 200</li>
                    <li>• Complexity: 50% | Risk: 40% | Environment: 70%</li>
                    <li className="font-semibold text-blue-900 mt-2">
                      → Weighted Hours = 200 × (1 + 0.50 + 0.40 + 0.70) = 200 × 2.60 = 520 hours
                    </li>
                  </ul>
                  <p className="text-xs text-gray-600 mt-3 italic">
                    This activity requires 2.6× the base hours due to its complexity, risk, and environmental burden.
                  </p>
                </div>
              </div>
            </div>

            {/* Best Practices */}
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-purple-900 mb-4">💡 Scoring Best Practices</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="bg-white p-4 rounded border border-purple-200">
                  <h4 className="font-semibold text-gray-900 mb-2">1. Be Consistent</h4>
                  <p className="text-gray-700">Score similar activities the same way. Use one person or team for all scoring.</p>
                </div>
                <div className="bg-white p-4 rounded border border-purple-200">
                  <h4 className="font-semibold text-gray-900 mb-2">2. Organization-Specific</h4>
                  <p className="text-gray-700">Scores reflect YOUR facility, not generic industry standards.</p>
                </div>
                <div className="bg-white p-4 rounded border border-purple-200">
                  <h4 className="font-semibold text-gray-900 mb-2">3. Evidence-Based</h4>
                  <p className="text-gray-700">Review past incidents, citations, and workers' comp claims.</p>
                </div>
                <div className="bg-white p-4 rounded border border-purple-200">
                  <h4 className="font-semibold text-gray-900 mb-2">4. Document Rationale</h4>
                  <p className="text-gray-700">Keep notes on why you assigned specific scores for audit trail.</p>
                </div>
                <div className="bg-white p-4 rounded border border-purple-200">
                  <h4 className="font-semibold text-gray-900 mb-2">5. Seek Input</h4>
                  <p className="text-gray-700">Consult operations managers and frontline staff who know the work.</p>
                </div>
                <div className="bg-white p-4 rounded border border-purple-200">
                  <h4 className="font-semibold text-gray-900 mb-2">6. Review Annually</h4>
                  <p className="text-gray-700">Scores change as regulations evolve and processes change.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Workflow Guide Tab */}
        {activeTab === 'workflow' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Application Workflow</h2>
            
            <div className="space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6">
                <h3 className="text-xl font-bold text-blue-900 mb-3">Step 1: Configure Facility</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Select facility type (General Industry or Academic Hospital)</li>
                  <li>• Enter employee count and organizational details</li>
                  <li>• Configure work hours, leave time, and admin percentage</li>
                  <li>• Set salary and benefits information</li>
                  <li>• For hospitals: Add bed count, square footage, building count, lab count</li>
                </ul>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6">
                <h3 className="text-xl font-bold text-green-900 mb-3">Step 2: Add Activities</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Click "📊 How to Score Activities" to review scoring criteria</li>
                  <li>• Add activities one at a time with base hours and risk scores</li>
                  <li>• For hospitals: Use pre-configured bundles to save time</li>
                  <li>• Review quick reference card in form for scoring help</li>
                  <li>• Enable/disable activities as needed for scenario testing</li>
                </ul>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-500 p-6">
                <h3 className="text-xl font-bold text-purple-900 mb-3">Step 3: Review Results</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• FTE requirements calculate automatically in real-time</li>
                  <li>• Review defensibility breakdown showing calculation details</li>
                  <li>• For hospitals: Compare to industry benchmarks</li>
                  <li>• Check efficiency metrics and cost analysis</li>
                  <li>• Click "📊 View Executive Report" for full justification</li>
                  <li>• Export to PDF for leadership presentations</li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
              <h3 className="text-lg font-bold text-yellow-900 mb-3">💡 Pro Tips</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• <strong>Save Scenarios:</strong> Test different staffing configurations by saving scenarios</li>
                <li>• <strong>Use Benchmarks:</strong> Hospital users should review benchmark comparison for validation</li>
                <li>• <strong>Document Everything:</strong> Keep notes on scoring rationale for future audits</li>
                <li>• <strong>Review Annually:</strong> Update your model yearly as conditions change</li>
              </ul>
            </div>
          </div>
        )}

        {/* Reports & Export Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Reports & Export Features</h2>
            
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6">
              <h3 className="text-xl font-bold text-blue-900 mb-4">📊 Executive Report</h3>
              <p className="text-gray-700 mb-4">
                The executive report provides a comprehensive, professional justification of your FTE requirements suitable for presentation to leadership, HR, or finance departments.
              </p>
              
              <div className="bg-white rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Report Sections:</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• <strong>Executive Summary:</strong> Key metrics and high-level justification</li>
                  <li>• <strong>Calculation Methodology:</strong> Detailed explanation of the risk-based model</li>
                  <li>• <strong>Workload Analysis:</strong> Complete breakdown of hours and activities</li>
                  <li>• <strong>Business Justification:</strong> ROI, compliance requirements, and risk mitigation</li>
                  <li>• <strong>Industry Benchmarks:</strong> Validation against healthcare standards (hospitals)</li>
                  <li>• <strong>Risks of Understaffing:</strong> Consequences of inadequate staffing</li>
                  <li>• <strong>Formal Recommendation:</strong> Clear staffing recommendation statement</li>
                  <li>• <strong>Disclaimer:</strong> Comprehensive legal and professional disclaimers</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">How to Use:</h4>
                <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside">
                  <li>Complete your facility configuration and add all activities</li>
                  <li>Review results in the dashboard</li>
                  <li>Click "📊 View Executive Report" button</li>
                  <li>Review the comprehensive report in the modal</li>
                  <li>Click "📄 Export PDF" to download a timestamped PDF version</li>
                  <li>Share PDF with stakeholders for budget approval</li>
                </ol>
              </div>
            </div>

            <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-6">
              <h3 className="text-xl font-bold text-purple-900 mb-4">📄 PDF Export Features</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>Professional Formatting:</strong> Multi-page document with color-coded sections</li>
                <li>• <strong>Complete Data:</strong> All calculations, tables, and justifications included</li>
                <li>• <strong>Timestamped:</strong> Filename includes date for version control</li>
                <li>• <strong>Print-Ready:</strong> Formatted for US Letter size (8.5" × 11")</li>
                <li>• <strong>Paginated:</strong> Automatic page numbers and footers</li>
              </ul>
            </div>

            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-6">
              <h3 className="text-xl font-bold text-green-900 mb-4">🎯 Presenting to Leadership</h3>
              <p className="text-gray-700 mb-3"><strong>Best Practices:</strong></p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• Start with the Executive Summary (page 1) for high-level overview</li>
                <li>• Reference the Methodology section to establish credibility</li>
                <li>• Highlight the Business Justification section for ROI discussion</li>
                <li>• Point to Industry Benchmarks (if applicable) for validation</li>
                <li>• Acknowledge the disclaimer proactively to show due diligence</li>
                <li>• Keep detailed workload breakdown available for technical questions</li>
              </ul>
            </div>
          </div>
        )}

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Q: How accurate are the FTE calculations?</h3>
                <p className="text-gray-700 text-sm">
                  The calculations are based on industry-standard methodologies and are as accurate as the input data. The model uses an additive risk weighting system that has been validated in EHS organizations. However, results should be reviewed by qualified EHS professionals and adjusted for organization-specific factors.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Q: Should I use the multiplicative or additive formula?</h3>
                <p className="text-gray-700 text-sm">
                  This application uses an <strong>additive model</strong> (1 + comp% + risk% + env%) which is more conservative and easier to justify. The additive approach is recommended for most organizations as it provides defensible FTE requirements without over-inflating needs.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Q: How do I determine the right percentages for my activities?</h3>
                <p className="text-gray-700 text-sm">
                  Use the Activity Scoring Guide (first tab in this help section). Review the characteristics and examples for each scoring tier. Consider your organization's specific risk profile, historical incidents, regulatory requirements, and consult with operations staff who understand the actual work involved.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Q: Can I use this for budget justification?</h3>
                <p className="text-gray-700 text-sm">
                  Yes! The Executive Report is specifically designed for budget presentations. It includes ROI analysis, cost-benefit information, risk mitigation justification, and industry benchmarks. The professional PDF export is suitable for presentation to leadership, HR, and finance departments.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Q: What's the difference between General Industry and Hospital modes?</h3>
                <p className="text-gray-700 text-sm">
                  Hospital mode includes additional calculations for fire safety hours (inspections, drills, Life Safety Code compliance) and environmental compliance hours (medical waste, wastewater, hazardous materials). It also provides industry benchmark comparisons based on bed count and employee population. General Industry mode uses standard calculations without these hospital-specific components.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Q: How often should I update my model?</h3>
                <p className="text-gray-700 text-sm">
                  Review and update your model at least annually, or whenever there are significant changes such as: new regulatory requirements, major process changes, organizational growth, new facilities, or shifts in risk profile. Keep your activity scores current to maintain accurate FTE projections.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Q: Are the calculations legally defensible?</h3>
                <p className="text-gray-700 text-sm">
                  The methodology is based on industry best practices and provides a defensible, data-driven approach. However, the report includes appropriate disclaimers and should not be considered legal advice. Staffing decisions should be made in consultation with qualified EHS professionals, legal counsel, and organizational leadership. The tool provides supporting documentation, not legal guarantees.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Documentation;
